// src/components/payment/SharePaymentLinkPopover.js
import React, { useMemo, useState, useEffect, useRef } from "react";
import { Button, Input, Label, Popover, PopoverBody, FormFeedback, FormGroup, Alert } from "reactstrap";
import axios from "axios";
import { API } from "../../api/urls";

const CHANNELS = { SMS: "SMS", EMAIL: "EMAIL", WHATSAPP: "WHATSAPP" };

const CHANNEL_LABELS = {
    [CHANNELS.SMS]: "SMS",
    [CHANNELS.EMAIL]: "E-posta",
    [CHANNELS.WHATSAPP]: "WhatsApp",
};

// Sunucu, seçilmeyen kanalları da "Atlandı/Hata" diye raporladığı ve gönderim
// başarısız olsa bile 200 döndürdüğü için sonucu yanıttaki bayraklardan okuyoruz.
const FAILURE_TEXTS = {
    [CHANNELS.SMS]:
        "SMS gönderilemedi. Numarayı kontrol edin; sorun sürerse SMS sağlayıcı (Netgsm) ayarlarınızı doğrulayın.",
    [CHANNELS.EMAIL]:
        "E-posta gönderilemedi. Adresi kontrol edin; sorun sürerse e-posta (SMTP) ayarlarınızı doğrulayın.",
    [CHANNELS.WHATSAPP]:
        "WhatsApp için numara bulunamadı. Numarayı elle girin veya cariye telefon tanımlayın.",
};

/** Seçilen kanal gerçekten gönderildi mi? */
const isChannelDelivered = (channel, data) => {
    if (channel === CHANNELS.SMS) return !!data?.smsSent;
    if (channel === CHANNELS.EMAIL) return !!data?.emailSent;
    return !!data?.whatsappNumber;
};

const isValidEmail = (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());
const isValidPhone = (v) => !v || /^[+]?[0-9]{7,15}$/.test(String(v).trim());

const SharePaymentLinkPopover = ({ isOpen, toggle, targetId, link, token, onSuccess }) => {
    const [channel, setChannel] = useState(CHANNELS.SMS);
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [alertMsg, setAlertMsg] = useState(null);
    const [alertType, setAlertType] = useState("info");
    // Yeni sekme engellenirse kullanıcıya tıklanabilir WhatsApp bağlantısı sunulur
    const [whatsappUrl, setWhatsappUrl] = useState(null);

    const hideMsgTimerRef = useRef(null);
    const autoCloseTimerRef = useRef(null);

    const needsPhone = channel === CHANNELS.SMS || channel === CHANNELS.WHATSAPP;
    const needsEmail = channel === CHANNELS.EMAIL;

    const emailValid = isValidEmail(email);
    const phoneValid = isValidPhone(phone);

    const disableSubmit = useMemo(() => submitting, [submitting]);

    useEffect(() => {
        if (isOpen) {
            setChannel(CHANNELS.SMS);
            setEmail("");
            setPhone("");
            setAlertMsg(null);
            setAlertType("info");
            setWhatsappUrl(null);
        }
        // cleanup: timerları temizle
        return () => {
            clearTimeout(hideMsgTimerRef.current);
            clearTimeout(autoCloseTimerRef.current);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event) => {
            const popoverElement = document.querySelector('.share-popover');
            const targetElement = document.getElementById(targetId);

            if (
                popoverElement &&
                !popoverElement.contains(event.target) &&
                targetElement &&
                !targetElement.contains(event.target)
            ) {
                toggle?.();
            }
        };

        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, toggle, targetId]);

    // Mesajları belirli süre sonra kaybet
    const showMessage = (msg, type = "info", { autoClose = false } = {}) => {
        setAlertMsg(msg);
        setAlertType(type);
        clearTimeout(hideMsgTimerRef.current);
        hideMsgTimerRef.current = setTimeout(() => {
            setAlertMsg(null);
        }, 3000); // 3 sn sonra mesajı kaldır

        // Başarıda modalı da kapat (isteğe bağlı)
        if (autoClose) {
            clearTimeout(autoCloseTimerRef.current);
            autoCloseTimerRef.current = setTimeout(() => {
                toggle?.();
            }, 2500);
        }
    };

    const handleShare = async () => {
        setAlertMsg(null);

        if (!link?.id) {
            showMessage("Link bilgisi bulunamadı.", "danger");
            return;
        }
        if (needsEmail && email && !emailValid) {
            showMessage("E-posta formatı geçersiz.", "danger");
            return;
        }
        if (needsPhone && phone && !phoneValid) {
            showMessage("Telefon formatı geçersiz. 7–15 hane, başına + yazılabilir.", "danger");
            return;
        }

        const body = {
            paymentLinkId: link.id,
            sendSms: channel === CHANNELS.SMS,
            sendEmail: channel === CHANNELS.EMAIL,
            sendWhatsapp: channel === CHANNELS.WHATSAPP,
            customPhoneNumber: needsPhone ? (phone?.trim() || null) : null,
            customEmail: needsEmail ? (email?.trim() || null) : null,
        };

        try {
            setSubmitting(true);
            const resp = await axios.post(API.payment_links_share, body);

            const { statusCode, message, data } = resp.data || {};

            if (statusCode === 200) {
                // 200 "gönderildi" demek değil; seçilen kanalın bayrağına bakılır
                if (!isChannelDelivered(channel, data)) {
                    showMessage(FAILURE_TEXTS[channel], "danger");
                    return;
                }

                if (channel === CHANNELS.WHATSAPP) {
                    const chosen = (phone?.trim() || data?.whatsappNumber || "").replace(/^\+/, "");
                    const waUrl = chosen && link.paymentLinkUrl
                        ? `https://wa.me/${chosen}?text=${encodeURIComponent(`Ödeme linkiniz: ${link.paymentLinkUrl}`)}`
                        : null;

                    if (!waUrl) {
                        showMessage(FAILURE_TEXTS[CHANNELS.WHATSAPP], "danger");
                        return;
                    }

                    // İstek tamamlandıktan sonra açılan pencere kullanıcı hareketi
                    // sayılmadığı için tarayıcı engelleyebiliyor; engellenirse
                    // bağlantıyı kullanıcının kendisi tıklayabilsin diye gösteriyoruz.
                    const opened = window.open(waUrl, "_blank");
                    if (!opened) {
                        setWhatsappUrl(waUrl);
                        showMessage("Tarayıcı yeni sekmeyi engelledi. Aşağıdaki bağlantıya dokunun.", "warning");
                        onSuccess?.({ ...data, channel });
                        return;
                    }
                }

                const target = channel === CHANNELS.EMAIL ? email.trim() : phone.trim();
                showMessage(
                    channel === CHANNELS.WHATSAPP
                        ? "WhatsApp açılıyor..."
                        : `${CHANNEL_LABELS[channel]} gönderildi${target ? ` → ${target}` : ""}`,
                    "success",
                    { autoClose: true }
                );

                onSuccess?.({ ...data, channel });
                // Formu temizle
                setChannel(CHANNELS.SMS);
                setEmail("");
                setPhone("");
            } else {
                // Örn: 404 "Ödeme linki için (manuel/fallback) geçerli telefon veya email bulunamadı."
                showMessage(message || "Paylaşım başarısız.", "danger");
            }
        } catch (err) {
            const msg = err?.response?.data?.message || "Paylaşım başarısız.";
            showMessage(msg, "danger");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Popover
            isOpen={isOpen}
            target={targetId}
            placement="left"
            trigger="manual"
            toggle={toggle}
            fade
            container="body"
            className="share-popover"
        >
            <PopoverBody className="p-3">
                <div className="d-flex flex-column gap-2">
                    <div className="fw-semibold mb-1">Linki Paylaş</div>

                    {alertMsg && (
                        <Alert color={alertType} className="py-2 mb-2">
                            {alertMsg}
                        </Alert>
                    )}

                    {whatsappUrl && (
                        <a
                            href={whatsappUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-success btn-sm mb-2"
                        >
                            <i className="bx bxl-whatsapp me-1" /> WhatsApp'ta Aç
                        </a>
                    )}

                    <Alert color="info" className="py-2 mb-2">
                        Manuel alanları boş bırakırsanız <strong>linkte atanan cari admin</strong> veya
                        <strong> link üzerinde belirtilen iletişim</strong> bilgileri esas alınacaktır.
                    </Alert>

                    {/* Kanal seçimi */}
                    <div className="d-flex flex-column gap-1">
                        {Object.values(CHANNELS).map((ch) => (
                            <div className="form-check" key={ch}>
                                <Input
                                    className="form-check-input"
                                    type="radio"
                                    id={`ch-${ch}-${link?.id}`}
                                    name="shareChannel"
                                    checked={channel === ch}
                                    onChange={() => setChannel(ch)}
                                />
                                <Label htmlFor={`ch-${ch}-${link?.id}`} className="form-check-label">
                                    {ch}
                                </Label>
                            </div>
                        ))}
                    </div>

                    {/* Seçime göre opsiyonel alanlar */}
                    {needsPhone && (
                        <FormGroup className="mt-2">
                            <Label className="form-label">Telefon (opsiyonel)</Label>
                            <Input
                                type="tel"
                                placeholder="+905xx..."
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                maxLength={15}
                                pattern="^[+]?[0-9]{7,15}$"
                                invalid={Boolean(phone) && !phoneValid}
                            />
                            {Boolean(phone) && !phoneValid && (
                                <FormFeedback>7–15 hane, + ile başlayabilir.</FormFeedback>
                            )}
                        </FormGroup>
                    )}

                    {needsEmail && (
                        <FormGroup className="mt-2">
                            <Label className="form-label">E-posta (opsiyonel)</Label>
                            <Input
                                type="email"
                                placeholder="ornek@firma.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                invalid={Boolean(email) && !emailValid}
                                spellCheck={false}
                                autoComplete="off"
                            />
                            {Boolean(email) && !emailValid && (
                                <FormFeedback>Geçerli bir e-posta adresi giriniz.</FormFeedback>
                            )}
                        </FormGroup>
                    )}

                    <div className="d-flex gap-2 mt-2">
                        <Button
                            outline
                            onClick={toggle}
                            disabled={submitting}
                            style={{
                                backgroundColor: "transparent",
                                borderColor: "#0A316E",
                                color: "#0A316E",
                                fontWeight: 500,
                            }}
                        >
                            İptal
                        </Button>
                        <Button
                            onClick={handleShare}
                            disabled={disableSubmit}
                            style={{
                                backgroundColor: "#FFA500",
                                borderColor: "#FFA500",
                                color: "white",
                                fontWeight: 500,
                            }}
                        >
                            {submitting ? "Gönderiliyor..." : "Gönder"}
                        </Button>
                    </div>
                </div>
            </PopoverBody>
        </Popover>
    );
};

export default SharePaymentLinkPopover;